import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from './UserContext';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, darkMode } = useUser();
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Initialize from localStorage first to prevent flash
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return 'dark';
    if (saved === 'light') return 'light';
    // Fallback to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Sync with user's darkMode preference from Firestore
  useEffect(() => {
    if (user && typeof darkMode === 'boolean') {
      setTheme(darkMode ? 'dark' : 'light');
      // Update localStorage to match Firestore
      localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    }
    // Don't reset theme if user is not loaded - keep current theme
  }, [user, darkMode]);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.classList.toggle('dark', theme === 'dark');
    
    // Always save to localStorage for persistence across sessions
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    const previousTheme = theme;
    
    // Update UI immediately for seamless experience
    setTheme(newTheme);
    
    // Update Firestore after UI change
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { darkMode: newTheme === 'dark' });
      } catch (error) {
        console.error('Failed to update darkMode in Firestore:', error);
        // Revert UI change if Firestore update fails
        setTheme(previousTheme);
      }
    }
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};